import React, { useEffect, useMemo, useRef, useState } from 'react';
import { bool, func, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import Icons from '../../Icons/Icons';
import { useIntl } from '../../../util/reactIntl';
import { searchAllContent } from '../../../ducks/globalSearch.duck';
import { upsertPreviousSearch } from '../../../util/api';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';

import css from './GlobalSearch.module.css';
import SearchPanel from './SearchPanel';

const buildResultLabel = r => {
  const type = r?.type;
  const a = r?.item?.attributes || {};
  return a.title || a.name || a.slug || type || '';
};

const buildResultPath = (routeConfiguration, r) => {
  const type = r?.type;
  const id = r?.item?.id;
  const a = r?.item?.attributes || {};
  const slug = a.slug;
  switch (type) {
    case 'blogs':
      if (id && slug)
        return createResourceLocatorString('SingleBlogPage', routeConfiguration, { id, slug }, {});
      return '/blogs';
    case 'tutorials':
      if (slug)
        return createResourceLocatorString('VideoPlayerPage', routeConfiguration, { slug }, {});
      return '/gallery';
    case 'events':
      if (a.title)
        return createResourceLocatorString(
          'EventDetailPage',
          routeConfiguration,
          { title: a.title },
          {}
        );
      return '/events';
    case 'topics':
    case 'topicDetails':
      if (id) {
        const topicId = a?.topic?.data?.id;
        const query = topicId ? `?topicId=${topicId}` : '';
        return `/resources/${id}${query}`;
      }
      if (slug) return `/written-guide/${slug}`;
      return '/resources';
    case 'theBasics':
      return '/resources';
    case 'symptoms':
      return '/resources/menopause#symptoms';
    case 'recipes':
      return '/resources';
    case 'products':
      if (slug) return `/products-and-treatments/${slug}`;
      return '/products-and-treatments';
    default:
      return '/search-content';
  }
};

const GlobalSearch = ({
  isExpanded,
  requiresAuthToExpand = true,
  forceCollapsed = false,
  onOpenChange,
  onPanelToggle,
  onValueChange,
  externalTerm,
}) => {
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const intl = useIntl();
  const results = useSelector(state => state.globalSearch?.results || []);
  const inProgress = useSelector(state => state.globalSearch?.inProgress);
  const currentUserId = useSelector(
    state => state.user?.currentUser?.id?.uuid || state.user?.currentUser?.id
  );
  const isAuthenticated = !!currentUserId;
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listboxId = 'header-search-list';
  const lastRequestedRef = useRef('');
  const showExpandedInput =
    isExpanded && !forceCollapsed && (isAuthenticated || !requiresAuthToExpand);

  // Notify parent of open state changes (for hiding other nav items)
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (forceCollapsed) {
      setOpen(false);
    }
  }, [forceCollapsed]);

  // Debounced search (idle 600ms, avoid duplicate dispatches)
  useEffect(() => {
    const term = value.trim();
    if (!term || term.length < 2) return;
    const rawLocale =
      (typeof window !== 'undefined' && window.localStorage.getItem('locale')) || 'el';
    const locale = rawLocale === 'el' ? 'el-GR' : rawLocale;
    const typeFilter = isAuthenticated ? undefined : 'blogs';
    const t = setTimeout(() => {
      if (lastRequestedRef.current !== term) {
        lastRequestedRef.current = term;
        dispatch(
          searchAllContent({
            q: term,
            defaultLocale: locale,
            limit: 10,
            offset: 0,
            type: typeFilter,
          })
        );
      }
    }, 600);
    return () => clearTimeout(t);
  }, [value, dispatch, isAuthenticated]);

  // External term selection (e.g., from SearchPanel history click)
  useEffect(() => {
    const term = (externalTerm || '').trim();
    if (!term || term === value) return;
    setValue(term);
    const rawLocale =
      (typeof window !== 'undefined' && window.localStorage.getItem('locale')) || 'el';
    const locale = rawLocale === 'el' ? 'el-GR' : rawLocale;
    lastRequestedRef.current = term;
    const typeFilter = isAuthenticated ? undefined : 'blogs';
    dispatch(
      searchAllContent({ q: term, defaultLocale: locale, limit: 10, offset: 0, type: typeFilter })
    );
    setOpen(true);
    inputRef.current?.focus();
  }, [externalTerm, dispatch, isAuthenticated]);

  // Close on outside click, but ignore clicks inside the integrated panel
  useEffect(() => {
    const onDocClick = e => {
      if (!containerRef.current) return;
      const inIntegratedPanel = e?.target?.closest?.('[data-header-search-panel="true"]');
      if (inIntegratedPanel) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Previous searches are rendered in the integrated panel; no local history here

  useEffect(() => {
    onValueChange?.(value);
    // Show integrated panel whenever the search is open (even with 0 chars)
    const active = !!open;
    onPanelToggle?.(active);
  }, [value, open, onValueChange, onPanelToggle]);

  const composedItems = useMemo(() => {
    const items = [];
    const trimmed = (value || '').trim();
    const minLen = 2;
    if (trimmed.length >= minLen) {
      const filteredResults = isAuthenticated ? results : results.filter(r => r?.type === 'blogs');

      const mapped = filteredResults.map(r => ({
        kind: 'result',
        label: buildResultLabel(r),
        path: buildResultPath(routeConfiguration, r),
      }));
      if (mapped.length) items.push({ kind: 'section', id: 'GlobalSearch.results' });
      items.push(...mapped);
    }
    return items;
  }, [value, results, routeConfiguration]);

  const handleSelect = item => {
    if (!item) return;
    // Navigate to the selected result
    if (item.path) {
      (async () => {
        try {
          const rawLocale =
            (typeof window !== 'undefined' && window.localStorage.getItem('locale')) || 'el';
          const language = rawLocale === 'el' ? 'el-GR' : rawLocale;
          const uid = window?.__PRELOADED_STATE__?.user?.currentUser?.id?.uuid;
          if (uid) {
            await upsertPreviousSearch({
              userId: String(uid),
              type: 'content',
              path: item.path,
              language,
              title: item.label,
              label: item.label,
            });
          }
        } catch (_) {}
      })();
      history.push(item.path);
    }
  };

  const onKeyDown = e => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Skip section headers
      setActiveIndex(i => {
        let next = Math.min(i + 1, composedItems.length - 1);
        while (next < composedItems.length && composedItems[next]?.kind === 'section') next++;
        return Math.min(next, composedItems.length - 1);
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => {
        let prev = Math.max(i - 1, 0);
        while (prev >= 0 && composedItems[prev]?.kind === 'section') prev--;
        return Math.max(prev, 0);
      });
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < composedItems.length) {
        const activeItem = composedItems[activeIndex];
        if (activeItem?.kind !== 'section') handleSelect(activeItem);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const onInputFocus = () => {
    setOpen(true);
  };

  const containerClass = classNames(css.container, {
    [css.collapsed]: !showExpandedInput,
  });

  return (
    <div className={containerClass} ref={containerRef}>
      {showExpandedInput ? (
        <div className={css.inputWrapper} onClick={() => setOpen(true)}>
          <span className={css.inputIcon}>
            <Icons name="search" />
          </span>
          <input
            ref={inputRef}
            className={css.input}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            value={value}
            onChange={e => setValue(e.target.value)}
            onFocus={onInputFocus}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-activedescendant={
              activeIndex >= 0 ? `${listboxId}-item-${activeIndex}` : undefined
            }
          />
          {value ? (
            <button className={css.clearBtn} onClick={() => setValue('')} aria-label="Clear">
              <Icons name="cross" />
            </button>
          ) : null}
        </div>
      ) : (
        <button
          className={css.iconButton}
          onClick={() => {
            setOpen(o => !o);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          aria-label="Search"
        >
          <Icons name="search" />
        </button>
      )}

      {/* Always show a dropdown under the input when open */}
      {(() => {
        const p = location?.pathname || '';
        const isBlog =
          p.startsWith('/blogs') ||
          p.startsWith('/en/blogs') ||
          p.startsWith('/de/blogs') ||
          p.startsWith('/blog/') ||
          p.startsWith('/en/blog/') ||
          p.startsWith('/de/blog/');
        const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
        // Render standalone dropdown on blog pages and mobile
        return open && (isBlog || isMobile) ? (
          <div className={isBlog ? css.dropdownBlog : css.dropdown} role="listbox" id={listboxId}>
            <SearchPanel
              value={value}
              onSelect={it => {
                if (it?.label) setValue(it.label);
                handleSelect(it);
              }}
            />
          </div>
        ) : null;
      })()}

      {/* Collapsed mode expanded input */}

      {/* Integrated panel is rendered by TopbarDesktop; input only here */}
      {!showExpandedInput && open ? (
        <div className={css.popover}>
          <div className={css.inputWrapper}>
            <span className={css.inputIcon}>
              <Icons name="search" />
            </span>
            <input
              ref={inputRef}
              className={css.input}
              placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
              value={value}
              onChange={e => setValue(e.target.value)}
              onFocus={onInputFocus}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-activedescendant={
                activeIndex >= 0 ? `${listboxId}-item-${activeIndex}` : undefined
              }
            />
            {value ? (
              <button className={css.clearBtn} onClick={() => setValue('')} aria-label="Clear">
                <Icons name="cross" />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

GlobalSearch.propTypes = {
  isExpanded: bool,
  requiresAuthToExpand: bool,
  forceCollapsed: bool,
  onOpenChange: func,
  onPanelToggle: func,
  onValueChange: func,
  externalTerm: string,
};

export default GlobalSearch;
